import { useEffect, useState } from "react";
import { useAppContext } from "../../../../classes/contexts/AppContext";
import { usePatientContext } from "../../../../classes/contexts/PatientContext";
import { useUser } from "../../../../classes/contexts/UserContext";
import { DataPermissions } from "../../../../classes/enums/PermissionsEnum";
import {
  filterByDateRange,
  formatClinicalDate,
  parseTimestampField,
} from "../../../../classes/utils/DateUtils";
import { filterBySearchTerm } from "../../../../classes/utils/SearchUtils";
import AddNew from "../../../../components/addNew/AddNew";
import { ErrorMessage } from "../../../../components/errorMessage/ErrorMessage";
import FilterAndActionBar from "../../../../components/filterAndActionBar.js/FilterAndActionBar";
import WidgetLoader from "../../../../components/widgetLoader/WidgetLoader";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import { useAuth } from "../../../../hooks/useAuth";
import { useRouteState } from "../../../../hooks/useRouteState";
import "../common/PatientWidgetLayout.css";
import usePatientWidgetControlsOffset from "../common/usePatientWidgetControlsOffset";
import "./ClinicalHistory.css";
import ClinicalHistoryItem from "./ClinicalHistoryItem";
import ClinicalHistoryModal from "./ClinicalHistoryModal";

const ClinicalHistory = () => {
  const {
    historiesByDate,
    clinicalHistoriesLoading,
    clinicalHistoriesError,
    refreshClinicalHistories,
    patient,
  } = usePatientContext();
  const { dateSettings } = useUser();
  const { showPopup } = useAppContext();
  const { checkPermissions } = useAuth();
  const { params } = useRouteState();
  const { t } = useTranslation();

  const [canImport] = useState(true);
  const [filteredHistories, setFilteredHistories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const canDelete = checkPermissions(DataPermissions.DELETE_CLINICAL_HISTORY);

  const getHistoryTimestamp = history => {
    const parsed = parseTimestampField(
      history?.createdAt || history?.updatedAt
    );
    return parsed ? parsed.getTime() : 0;
  };

  useEffect(() => {
    if (historiesByDate && Array.isArray(historiesByDate)) {
      const sorted = [...historiesByDate].sort(
        (a, b) => getHistoryTimestamp(b) - getHistoryTimestamp(a)
      );
      let filtered = sorted;

      // Apply search filter - full text search
      filtered = filterBySearchTerm(
        filtered,
        history => {
          let text = "";
          if (history.createdAt) {
            const date = parseTimestampField(history.createdAt);
            if (date) {
              text += date.toString() + " ";
              text +=
                (formatClinicalDate(date, { locale: dateSettings?.locale }) ||
                  "") + " ";
            }
          }
          if (history.weight) text += history.weight.toString() + " ";
          if (history.height) text += history.height.toString() + " ";
          if (Array.isArray(history.conditions) && history.conditions.length)
            text += history.conditions.join(" ") + " ";
          if (history.note) text += history.note + " ";
          if (history.qualityOfLifeIndicatorsData?.length) {
            history.qualityOfLifeIndicatorsData.forEach(indicator => {
              text += `${indicator.name || ""} ${indicator.value || ""} `;
            });
          }
          return text;
        },
        searchTerm
      );

      // Apply date range filter
      if (dateRange && (dateRange.start || dateRange.end)) {
        filtered = filterByDateRange(
          filtered,
          item => item.createdAt,
          dateRange.start,
          dateRange.end
        );
      }

      setFilteredHistories(filtered);
    } else {
      setFilteredHistories([]);
    }
  }, [historiesByDate, searchTerm, dateRange, dateSettings]);

  const handleSearch = term => {
    setSearchTerm(term || "");
  };

  const handleDateRangeChange = (start, end) => {
    setDateRange({ start, end });
  };

  const clearFilters = () => {
    setSearchTerm("");
    setDateRange({ start: null, end: null });
  };

  const { controlsRef, shellStyle } = usePatientWidgetControlsOffset();

  const openAddClinicalHistory = () => {
    showPopup(<ClinicalHistoryModal patientId={patient?._id} />);
  };

  const renderContent = () => {
    if (clinicalHistoriesLoading) {
      return <WidgetLoader isLoading={true} />;
    }

    if (clinicalHistoriesError) {
      return <ErrorMessage message={t("311")} />;
      {
        /* "Failed to load clinical histories" */
      }
    }

    if (!filteredHistories || filteredHistories.length === 0) {
      return (
        <AddNew
          elementTitle={t("301")}
          onClick={canImport ? openAddClinicalHistory : null}
        />
      );
      {
        /* "Clinical History" */
      }
    }

    return (
      <div className="clinical-history-list">
        {filteredHistories.map(history => (
          <ClinicalHistoryItem
            key={history._id}
            history={history}
            canDelete={canDelete}
            onHistoryDeleted={refreshClinicalHistories}
          />
        ))}
      </div>
    );
  };

  return (
    <div
      className="clinical-history-container patient-widget-shell"
      key={params.patientId}
      style={shellStyle}
    >
      <div className="clinical-history-content patient-widget-content">
        {renderContent()}
      </div>

      <div
        className="clinical-history-controls patient-widget-controls"
        ref={controlsRef}
      >
        <FilterAndActionBar
          onSearch={handleSearch}
          onDateRangeChange={handleDateRangeChange}
          onClear={clearFilters}
          onAdd={canImport ? openAddClinicalHistory : null}
          value={searchTerm}
        />
        {/* "Search clinical histories..." */}
      </div>
    </div>
  );
};

export default ClinicalHistory;
